import type { PublicPlan } from '../../api/site';

/**
 * How a plan is written on the public pages — the pricing cards and the
 * signup page's plan column (R13) say the same numbers the same way.
 */

export const fmtMoney = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);

export const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);

/** Matches SIGNUP_TRIAL_DAYS on the server — every paid plan, the same trial. */
export const TRIAL_DAYS = 14;

/** A ceiling as the card says it. null = no ceiling at all. */
export function limitLine(v: number | null, singular: string, plural: string): string {
  if (v === null) return `Unlimited ${plural}`;
  return `${fmtNum(v)} ${v === 1 ? singular : plural}`;
}

export function storageLine(bytes: number | null): string {
  if (bytes === null) return 'Unlimited file storage';
  const gb = bytes / 1_000_000_000;
  return gb >= 1 ? `${fmtNum(gb)} GB file storage` : `${fmtNum(bytes / 1_000_000)} MB file storage`;
}

/**
 * Every hand-written limit line, in the card's order (CLAUDE.md, "A plan is
 * shown in more places than it is enforced", item 7: a new LIMIT is a line
 * here). `short` is the first three — the ones the signup column leads with.
 */
export function planLimitLines(p: PublicPlan): string[] {
  return [
    limitLine(p.limits.forms, 'form', 'forms'),
    limitLine(p.limits.submissionsPerMonth, 'submission / month', 'submissions / month'),
    limitLine(p.limits.users, 'user', 'users'),
    storageLine(p.limits.storageBytes),
    limitLine(p.limits.submissionRecords, 'stored submission', 'stored submissions'),
    limitLine(p.limits.fieldsPerForm, 'field or widget per form', 'fields and widgets per form'),
  ];
}

/**
 * The Annual toggle's saving, from the plans themselves (playbook §4: the real
 * per-plan discount, never a fixed 20%). "save 20%" only when every paid plan
 * saves 20% by paying yearly; "save up to 25%" when any saves less — or
 * nothing, or cannot be bought yearly at all; nothing when none saves.
 */
export function annualSaving(plans: PublicPlan[]): string {
  const pcts = plans
    .filter((p) => p.price_monthly_cents !== null)
    .map((p) => (p.price_annual_cents !== null ? Math.max(0, p.annual_discount_pct) : 0));
  const max = Math.max(0, ...pcts);
  if (max === 0) return '';
  return pcts.every((p) => p === max) ? `save ${max}%` : `save up to ${max}%`;
}

/**
 * "Oct 10" — the day a trial that starts now would first charge, if the
 * customer keeps it. An estimate by construction: the trial starts when
 * Checkout completes, which is normally a minute from now.
 */
export function firstChargeDate(now = new Date(), days = TRIAL_DAYS): string {
  const d = new Date(now.getTime() + days * 86_400_000);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}
